import React, { ReactElement } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { enviroment } from "../../../enviroment";
import {
  statusBadgeDotClass,
  statusBadgeLayoutClass,
} from "../../../utils/functions/statusBadgeWrap";

// "Prices on their way" for a logo LIST row whose price ladder the API HELD
// (flag REACT_APP_LOGO_PRICE_HELD_ENABLED; API flag
// PriceCurrencyListHoldEnabled, js-kitmaker-api#1295: LogoDto.priceHeld =
// true + product.prices = [] while the supplier's rows for a new logo are
// not stored yet and the stored template rows are in a foreign currency -
// the EUR ladders Nichlas saw on 1105611/1105612, 31/10). The row's price
// cell shows this line instead of nothing; the list re-reads itself
// (useLogoListPendingPoll) and a landed popup / cart GetLogo patches the row
// (logoDetailCache, flag B), so the line turns into the DKK ladder without
// F5. The popup's price column shows the same line while it still renders
// the held list row (before its own GetLogo lands). Renders NOTHING with the
// flag off - the flag-off DOM is byte-identical.
//
// Look: the same status-line family as ProductPendingBadge (10 px semibold,
// the participants' 0.25 rem dot in front, gap-2, no pill) so the two states
// read as siblings in the same cell - with two differences the a11y lens
// asked for (31/10): the TEXT is text-gray-600 (#596675: 5.9:1 on white,
// 5.1:1 on the hovered row; the pending tone #E7B400 is 1.9:1 at 10 px and
// fails WCAG 1.4.3 - the amber DOT keeps the "waiting" tone), and the dot's
// motion-safe pulse runs 13 x 2 s = the list poll's fast 26 s re-read phase
// and then rests (WCAG 2.2.2: a "working" signal must not run on for
// minutes; the poll's two slow ticks at 60 / 330 s and its restart on a
// return to the tab run without animation - the text carries the state on
// its own). The caller never
// renders both badges: a productPending row keeps its own
// (isLogoProductPending is judged first).
//
// `wrap` (flag REACT_APP_LOGO_STATUS_BADGE_WRAP_ENABLED, statusBadgeWrap):
// the list row's price cell (1/12 of the row, ~99-112 px) passes it so the
// text wraps inside the cell instead of running into the storage column;
// with that flag off the prop is inert (today's nowrap classes).
//
// Accessibility: plain text, no live region. A region inserted together
// with its text is skipped by most reader/browser pairs (the lesson of
// CartPriceUpdatedNote and logo-search-status), and one region per held row
// would be noise; a reader finds the state in the price cell like any other
// cell text. The Tooltip is a DESCRIPTION (describeChild): MUI hands its
// title to the span as the native title / aria-describedby, so the
// accessible name stays the visible "Prices on their way" and the long help
// text describes it. The dot is decoration (aria-hidden). No timer, no
// state, no call.
export const PRICE_HELD_DOT_PULSE =
  "motion-safe:animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_13]";

export function PriceHeldBadge({
  className,
  wrap,
}: {
  className?: string;
  wrap?: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!enviroment.logoPriceHeldEnabled) return null;
  return (
    <Tooltip title={t("price_held_tooltip")} placement="top" describeChild>
      <span
        data-testid="price-held-badge"
        className={`${statusBadgeLayoutClass(wrap)} text-xs font-semibold text-gray-600 ${
          className ?? ""
        }`}
      >
        <span
          aria-hidden="true"
          className={`h-[0.25rem] w-[0.25rem] ${statusBadgeDotClass(wrap)} rounded-full bg-status-pending-1 ${PRICE_HELD_DOT_PULSE}`}
        />
        {t("price_held")}
      </span>
    </Tooltip>
  );
}
